import { apiUrl } from './config'

/**
 * Browser log capture that batches errors/warnings and ships them to the CRM
 * API (`/api/v1/logging`), which forwards to Grafana Loki — the same pipeline
 * the existing Order Web client logging uses.
 */
interface LogEntry {
  timestamp: string
  level: 'error' | 'warn' | 'info'
  message: string
  metadata: {
    userAgent: string
    sessionId: string
    url: string
    client: string
    stackTrace?: string
    errorName?: string
  }
}

const FLUSH_INTERVAL_MS = 5000
const MAX_QUEUE = 20
const queue: LogEntry[] = []
const sessionId = `crm-${Math.random().toString(36).slice(2, 12)}`

function enqueue(level: LogEntry['level'], message: string, extra: Partial<LogEntry['metadata']> = {}) {
  queue.push({
    timestamp: new Date().toISOString(),
    level,
    message: String(message).slice(0, 2000),
    metadata: {
      userAgent: navigator.userAgent,
      sessionId,
      url: window.location.href,
      client: 'crm',
      ...extra,
    },
  })
  if (queue.length >= MAX_QUEUE) void flush()
}

async function flush() {
  if (queue.length === 0) return
  const batch = queue.splice(0, queue.length)
  try {
    await fetch(apiUrl('/api/v1/logging'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logs: batch }),
      keepalive: true,
    })
  } catch {
    // Swallow: logging must never break the app.
  }
}

export function initClientLogging() {
  window.addEventListener('error', (e) => {
    enqueue('error', e.message, { stackTrace: e.error?.stack, errorName: e.error?.name })
  })
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason
    enqueue('error', reason?.message ?? String(reason), { stackTrace: reason?.stack, errorName: reason?.name })
  })

  const originalError = console.error.bind(console)
  console.error = (...args: unknown[]) => {
    enqueue('error', args.map(String).join(' '))
    originalError(...args)
  }

  setInterval(() => void flush(), FLUSH_INTERVAL_MS)
  window.addEventListener('beforeunload', () => void flush())
}
